/**
 * Математика вилок на клиенте — зеркало backend/forq/money.py.
 *
 * decimal.js, а не number: 0.1 + 0.2 !== 0.3, и в цепочке кэф → ставка → ROI
 * это вылезает в третьем знаке. Пользователь сверяет наше число со своим
 * калькулятором, и расхождение читается как «сканер врёт».
 *
 * Контрольные примеры в calc.test.ts обязаны совпадать с
 * backend/tests/test_money.py. Разошлись — это регрессия, а не мелочь.
 */

import { Decimal } from 'decimal.js';

Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

export type Odds = Decimal | string;

/**
 * Разобранные строки: одинаковая строка отдаёт ОДИН и тот же объект.
 *
 * `Decimal` неизменяем — все действия возвращают новое число, — поэтому общий
 * объект безопасен, а разбор строки не бесплатен. Считать было бы незачем, если
 * бы не поиск объёма: он гоняет `quoteAtVolume` десятки раз на вилку, тот
 * делает до восьми проходов, и каждый проход заново превращал в числа весь
 * стакан. Профиль от 06.08.2026 (396 вилок, пять кадров): разбор и создание
 * чисел съедали пятую часть времени, ещё 6% уходило на сборку мусора от них.
 *
 * Кэш ограничен: цены и размеры приходят из чужих стаканов, и их множество не
 * ограничено ничем. Переполнение сбрасывает кэш целиком — это дешевле учёта
 * возрастов и не может протухнуть.
 */
const PARSED_LIMIT = 20000;
const parsedCache = new Map<string, Decimal>();

const d = (v: Odds): Decimal => {
  if (v instanceof Decimal) return v;
  const hit = parsedCache.get(v);
  if (hit !== undefined) return hit;
  const made = new Decimal(v);
  if (parsedCache.size >= PARSED_LIMIT) parsedCache.clear();
  parsedCache.set(v, made);
  return made;
};

/** S = Σ 1/odds. S < 1 — вилка. */
export function impliedSum(odds: Odds[]): Decimal {
  return odds.reduce<Decimal>((acc, o) => acc.plus(new Decimal(1).div(d(o))), new Decimal(0));
}

/**
 * Брутто-ROI как доля: 1/S − 1.
 *
 * Кэфы приводятся к экранным (три знака, вниз) ДО расчёта — зеркало
 * `money.roi_gross` на бэкенде. Без этого две стороны считают одну величину по
 * разным входным данным и законно расходятся: замер на проде 31.07.2026 дал
 * шесть вилок из тринадцати с разницей в первом знаке процента. Карточка при
 * этом предлагает брутто прямым текстом для сверки с калькулятором
 * пользователя, то есть расхождение видно первому, кто решит проверить.
 */
export function roiGross(odds: Odds[]): Decimal {
  return new Decimal(1).div(impliedSum(odds.map(oddsShown))).minus(1);
}

/** Кэф числом ровно в том виде, в каком он на экране. Пара к `oddsText`. */
export function oddsShown(value: Odds): Decimal {
  return d(value).toDecimalPlaces(3, Decimal.ROUND_DOWN);
}

/** Раскладка банка: выплаты по всем исходам равны. */
export function stakes(bank: Odds, odds: Odds[]): Decimal[] {
  const s = impliedSum(odds);
  return odds.map((o) => d(bank).times(new Decimal(1).div(d(o))).div(s));
}

/** Выплата к показу — округление ВНИЗ: завышать ожидание нельзя. */
export function payout(stake: Odds, odds: Odds): Decimal {
  return d(stake).times(d(odds)).toDecimalPlaces(0, Decimal.ROUND_DOWN);
}

/**
 * Кэф для экрана — зеркало money.odds_str: три знака, вниз, без экспоненты.
 *
 * Форматирует клиент, хотя сервер уже присылает готовую строку: во время выката
 * несколько секунд отвечает предыдущая версия API, а вкладка сканера живёт
 * неделями и может застать любую. Печатать «1E+1» в колонке кэфа нельзя ни в
 * одной из этих секунд.
 */
export function oddsText(value: Odds): string {
  return oddsShown(value).toFixed(3);
}

export interface StakePlan {
  stakes: Decimal[];
  payouts: Decimal[];
  total: Decimal;
  /** Фактический ROI ПОСЛЕ округления — именно он идёт в карточку. */
  roi: Decimal;
  roundedLeg: number;
}

/**
 * Округлить ставку на якорной ноге до шага и пересчитать остальные.
 *
 * Ставка 1001.11 в РУ-конторе — метка арбитражника, её видит трейдер. Поэтому
 * округляем, но обязаны показать ROI после округления, а не исходный.
 */
export type RoundDir = 'down' | 'near';

export function roundAndRebalance(
  bank: Odds,
  odds: Odds[],
  step: Odds,
  anchor = 0,
  direction: RoundDir = 'down',
): StakePlan {
  const raw = stakes(bank, odds);
  const stepD = d(step);
  const mode = direction === 'near' ? Decimal.ROUND_HALF_UP : Decimal.ROUND_DOWN;
  let anchorStake = raw[anchor]!.div(stepD).toDecimalPlaces(0, mode).times(stepD);
  if (anchorStake.lte(0)) anchorStake = stepD;

  const targetPayout = anchorStake.times(d(odds[anchor]!));
  const out = odds.map((o, i) =>
    i === anchor ? anchorStake : targetPayout.div(d(o)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP),
  );
  const payouts = out.map((s, i) => s.times(d(odds[i]!)));
  const total = out.reduce((a, b) => a.plus(b), new Decimal(0));
  // Гарантированная прибыль определяется ХУДШИМ исходом, а не средним.
  const worst = payouts.reduce((a, b) => (a.lt(b) ? a : b));
  return { stakes: out, payouts, total, roi: worst.div(total).minus(1), roundedLeg: anchor };
}

/** Кэф после издержек: комиссия, проскальзывание, конвертация. */
export function effectiveOdds(odds: Odds, costFraction: Odds): Decimal {
  return d(odds).times(new Decimal(1).minus(d(costFraction)));
}

/**
 * NET-ROI — основное число в ленте. Брутто показываем рядом, ради сверки.
 *
 * Число издержек обязано совпасть с числом кэфов. Раньше недостающая стоимость
 * молча заменялась нулём (`costs[i] ?? '0'`), и это ровно тот случай, когда
 * тишина дороже падения: нога без комиссии выглядит выгоднее, чем есть, а NET
 * уходит вверх — то самое «показывать брутто значит продавать убыток», против
 * которого и заведено это число. Бэкенд на таком входе падает
 * (`zip(..., strict=True)` в `money.roi_net`); зеркало обязано вести себя так же,
 * иначе стороны разойдутся именно там, где расхождение стоит денег.
 */
export function roiNet(odds: Odds[], costs: Odds[]): Decimal {
  if (odds.length !== costs.length) {
    throw new Error(
      `roiNet: ${odds.length} кэфов против ${costs.length} издержек — ` +
        'пропущенная стоимость завысила бы NET',
    );
  }
  return roiGross(odds.map((o, i) => effectiveOdds(o, costs[i]!)));
}

// ── Polymarket ──

export const oddsFromSharePrice = (price: Odds): Decimal => new Decimal(1).div(d(price));

/** Целые шары, остаток не тратим. */
export const sharesForStake = (stake: Odds, price: Odds): Decimal =>
  d(stake).div(d(price)).toDecimalPlaces(0, Decimal.ROUND_DOWN);

/**
 * Ставка комиссии категории «спорт». Запасное значение: настоящую ставку отдаёт
 * сам рынок (feeSchedule.rate), и часть рынков от комиссии освобождена.
 */
export const SPORTS_FEE_RATE = '0.05';

/**
 * Комиссия taker в долларах на один шар: rate × p × (1 − p).
 * Максимум на цене 0.50 — 1.25 доллара на 100 шаров для спорта.
 * Правка формулы идёт вместе с backend/forq/money.py одним коммитом, иначе фронт
 * и бэкенд покажут разный NET.
 */
export function takerFeePerShare(
  price: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
  exponent = 1,
): Decimal {
  const p = d(price);
  return d(feeRate).times(p.times(new Decimal(1).minus(p)).pow(exponent));
}

/**
 * Комиссия как доля вложенных денег — в этом виде идёт в roiNet.
 * При exponent = 1 это rate × (1 − p): в долларах симметрично с пиком на 0.50, а
 * в процентах от ставки дешёвый шар стоит дороже.
 */
const costFractionCache = new WeakMap<Decimal, Map<string, Decimal>>();

export const takerCostFraction = (
  price: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
  exponent = 1,
): Decimal => {
  // Комиссия зависит только от цены уровня и расписания рынка, а проход по
  // стакану спрашивает её об одном и том же уровне на каждом проходе поиска
  // объёма. Возведение в степень при этом самое дорогое действие в формуле.
  // Ключ — сам объект цены: строки стакана дают общий объект (см. `d`).
  const p = d(price);
  const key = `${feeRate instanceof Decimal ? feeRate.toString() : feeRate}|${exponent}`;
  let byRate = costFractionCache.get(p);
  if (byRate === undefined) {
    byRate = new Map();
    costFractionCache.set(p, byRate);
  }
  const hit = byRate.get(key);
  if (hit !== undefined) return hit;
  const made = takerFeePerShare(p, feeRate, exponent).div(p);
  byRate.set(key, made);
  return made;
};

/**
 * Predict.fun: базовая ставка × min(p, 1 − p) на шар (docs.predict.fun, «Predict
 * Fees and Limits»). Здесь ставка — доля (0.02 = 200 bps), как `feeRate` ноги.
 * Пара к `money.predict_fee_per_share` — считать обязаны одинаково.
 */
export function predictFeePerShare(price: Odds, feeRate: Odds): Decimal {
  const p = d(price);
  return d(feeRate).times(Decimal.min(p, new Decimal(1).minus(p)));
}

export const predictCostFraction = (price: Odds, feeRate: Odds): Decimal =>
  predictFeePerShare(price, feeRate).div(d(price));

/** Модель комиссии биржевой ноги: `min` — Predict.fun, иначе кривая Polymarket. */
export type FeeModel = 'min';

export const legCostFraction = (
  price: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
  exponent = 1,
  model?: FeeModel,
): Decimal => (model === 'min' ? predictCostFraction(price, feeRate) : takerCostFraction(price, feeRate, exponent));

/** Комиссия удерживается шарами: на руки приходит меньше, чем куплено. */
export const takerFeeInShares = (
  shares: Odds,
  price: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
  exponent = 1,
  model?: FeeModel,
): Decimal => d(shares).times(legCostFraction(price, feeRate, exponent, model));

/** Сколько шаров останется после удержания комиссии — это число идёт в карточку. */
export const sharesAfterFee = (
  shares: Odds,
  price: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
  exponent = 1,
  model?: FeeModel,
): Decimal => d(shares).minus(takerFeeInShares(shares, price, feeRate, exponent, model));

/**
 * Итог ноги биржи, если рынок решится 50/50 (матч или карта не состоялись):
 * шар выплачивает 0.5, контора в том же случае возвращает ставку.
 */
export const voidPnl = (stake: Odds, price: Odds): Decimal =>
  d(stake).times(new Decimal('0.5').div(d(price)).minus(1)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

/** Проценты с двумя знаками, как в бейдже карточки. */
export const pct = (fraction: Decimal): string => fraction.times(100).toFixed(2);

// ── Шаги показанных чисел — зеркало раздела «Шаги» в money.py ──
//
// Строка ленты не отчёт, а инструкция: оператор переписывает числа в две чужие
// формы. Форма конторы не примет ставку 1001.11, форма биржи — цену 0.4374 и
// 132.66 шара. Округляем мы, и показываем результат ПОСЛЕ округления, иначе
// человек введёт свои числа, получит другой ROI и будет прав.
//
// Направление у каждого шага против нас: ставка вниз, цена шара вверх, шары
// вниз, доллары вверх. Производные считаются от округлённых, а не от VWAP.

export const RUB_STAKE_STEP = '10';
export const SHARE_STEP = '0.1';

export interface BookTicket {
  price: Decimal;
  shares: Decimal;
  dollars: Decimal;
}

export interface BookmakerTicket {
  stake: Decimal;
  payout: Decimal;
}

/** Цена шара к целому центу, ВВЕРХ: тик биржи — цент, дробной цены там нет. */
export const priceToCent = (value: Odds): Decimal => d(value).toDecimalPlaces(2, Decimal.ROUND_UP);

/** Шары к десятой доле, ВНИЗ. */
export const sharesShown = (value: Odds): Decimal =>
  d(value).div(SHARE_STEP).toDecimalPlaces(0, Decimal.ROUND_DOWN).times(SHARE_STEP);

/** Билет биржевой ноги: цена вверх, шары вниз, расход — из них двоих. */
export function bookTicket(price: Odds, shares: Odds): BookTicket {
  const priceC = priceToCent(price);
  const sharesR = sharesShown(shares);
  return {
    price: priceC,
    shares: sharesR,
    dollars: priceC.times(sharesR).toDecimalPlaces(2, Decimal.ROUND_UP),
  };
}

/**
 * Билет ноги у конторы: ставка вниз до шага, выплата — от округлённой ставки.
 *
 * Шаг в десять рублей взят не ради красоты. Ставка 1001.11 — метка
 * арбитражника: трейдер видит её в потоке и режет лимит счёту.
 */
export function bookmakerTicket(stake: Odds, odds: Odds, step: Odds = RUB_STAKE_STEP): BookmakerTicket {
  const stepD = d(step);
  let stakeR = d(stake).div(stepD).toDecimalPlaces(0, Decimal.ROUND_DOWN).times(stepD);
  if (stakeR.lte(0)) stakeR = stepD;
  return { stake: stakeR, payout: payout(stakeR, odds) };
}

// ── Нога «ПРОТИВ» на бирже (lay) ──
//
// Человек принимает чужую ставку, как букмекер, и деньги устроены наоборот:
// со счёта уходит РИСК — ставка соперника × (кэф − 1), — а выигрыш равен ставке
// соперника за вычетом комиссии биржи. Считать такую ногу обычной ставкой
// значит завысить прибыль в разы: замер 07.08.2026 — 185% на экране против
// честных 36% (вилка 28.5 / против 3.2).

/**
 * Комиссия биржи с выигрыша ноги «против». Пять процентов — не догадка:
 * процент донора (36.33 на паре 28.5/3.2) воспроизводится до сотой ровно при
 * этой ставке; она же — базовая комиссия betfair-подобных бирж.
 */
export const LAY_FEE_RATE = '0.05';

/**
 * Кэф ноги «против», приведённый к обычной ставке размером в риск:
 * 1 + (1 − fee)/(кэф − 1). С ним распределение банка и вершина объёма работают
 * без ветвлений — нога отличается только билетом.
 */
export const layOdds = (odds: Odds, feeRate: Odds = LAY_FEE_RATE): Decimal =>
  new Decimal(1).plus(new Decimal(1).minus(d(feeRate)).div(d(odds).minus(1)));

export interface LayTicket {
  /** Что ввести на бирже: ставка соперника, которую принимаем. */
  stake: Decimal;
  /** Что биржа заморозит и что уйдёт при проигрыше: ставка × (кэф − 1). */
  risk: Decimal;
  /** Вернётся при выигрыше: риск обратно плюс ставка соперника без комиссии. */
  payout: Decimal;
}

/**
 * Билет ноги «против»: округляется СТАВКА соперника — именно её вводят в форму
 * биржи, — риск и выплата следуют за округлённой. Ставка вниз: риск получается
 * меньше выделенного объёма, а не больше банка.
 */
export function layTicket(
  risk: Odds,
  odds: Odds,
  step: Odds = RUB_STAKE_STEP,
  feeRate: Odds = LAY_FEE_RATE,
): LayTicket {
  const L = d(odds);
  const stepD = d(step);
  let stakeR = d(risk).div(L.minus(1)).div(stepD).toDecimalPlaces(0, Decimal.ROUND_DOWN).times(stepD);
  if (stakeR.lte(0)) stakeR = stepD;
  const riskR = stakeR.times(L.minus(1));
  return {
    stake: stakeR,
    risk: riskR,
    payout: riskR.plus(stakeR.times(new Decimal(1).minus(d(feeRate)))),
  };
}

// ── Проход по стакану — зеркало backend/forq/engine/pricing.py ──
//
// Здесь эта математика живёт не для красоты симметрии. Банк, порог, допущенный
// потолок и курс — регуляторы пользователя, и пересчитывать строку на сервере
// при каждом движении ползунка значит ждать сеть там, где ждать нечего. Поэтому
// уровни стакана едут в протоколе, а вершина ищется здесь.
//
// Расхождение с бэкендом во втором знаке — регрессия. Контрольные примеры в
// calc.test.ts и backend/tests/test_money.py идут от одних входов.

/** Уровень стакана: цена и объём в шарах, от лучшей цены к худшей. */
export type BookLevel = readonly [string, string];

export type Leg =
  | {
      kind: 'book';
      asks: readonly BookLevel[];
      feeRate?: string;
      feeExponent?: number;
      feeModel?: FeeModel;
      minOrder?: string;
      /** Баланс кошелька в рублях расчёта: больше этого с ноги не уйдёт. */
      limit?: string | null;
    }
  | { kind: 'fixed'; odds: string; limit?: string | null };

export interface LegFill {
  stake: Decimal;
  /** Кэф ДО комиссии: 1 / средняя цена исполнения. */
  odds: Decimal;
  costFraction: Decimal;
  shares: Decimal | null;
  cappedBy: string | null;
  /** Худший забираемый уровень — это число идёт в лимитный ордер. */
  limitPrice: Decimal | null;
  /** Средневзвешенная цена — это то, что спишется. */
  avgPrice: Decimal | null;
}

export interface VolumeQuote {
  requested: Decimal;
  fills: LegFill[];
  roi: Decimal;
  total: Decimal;
  profit: Decimal;
  limitedBy: string[];
}

const DEFAULT_CEILING = '10000';
const VOLUME_TOLERANCE = '1';
/** Двенадцать шагов ужимают отрезок до 0.4% ширины — рядом с вершиной этого хватает. */
const GOLDEN_STEPS = 12;

const effOdds = (fill: LegFill): Decimal => effectiveOdds(fill.odds, fill.costFraction);
const fillPayout = (fill: LegFill): Decimal => fill.stake.times(effOdds(fill));

export function bestOdds(leg: Leg): Decimal {
  if (leg.kind === 'fixed') return d(leg.odds);
  const first = leg.asks[0];
  if (!first) throw new Error('нога без стакана: кэф брать неоткуда');
  return new Decimal(1).div(d(first[0]));
}

/**
 * Проход по уровням: сколько шаров и по какой средней цене возьмёт этот бюджет.
 *
 * Потраченное на закрывающем уровне ПРИСВАИВАЕТСЯ, а не доливается. Умножение
 * частного на делитель возвращает крошку в последнем знаке, и из-за неё цикл
 * заходил на следующий уровень: лимитной ценой объявлялось 0.46 вместо 0.44, то
 * есть оператору предлагалось переплатить два цента на ровном месте.
 */
export function walkBook(leg: Extract<Leg, { kind: 'book' }>, budget: Odds): LegFill {
  const asked = d(budget);
  const limit = leg.limit == null ? null : d(leg.limit);
  const capped = limit !== null && limit.gt(0) && asked.gt(limit);
  const budgetD = capped ? limit! : asked;
  if (leg.asks.length === 0) throw new Error('нога без стакана: кэф брать неоткуда');
  const feeRate = leg.feeRate ?? SPORTS_FEE_RATE;
  const feeExponent = leg.feeExponent ?? 1;
  const minOrder = d(leg.minOrder ?? '0');

  let shares = new Decimal(0);
  let spent = new Decimal(0);
  let feeShares = new Decimal(0);
  let worst: Decimal | null = null;

  for (const [price, size] of leg.asks) {
    const room = budgetD.minus(spent);
    if (room.lte(0)) break;
    const priceD = d(price);
    const sizeD = d(size);
    const wanted = room.div(priceD);
    let take: Decimal;
    if (wanted.lte(sizeD)) {
      take = wanted;
      spent = budgetD;
    } else {
      take = sizeD;
      spent = spent.plus(take.times(priceD));
    }
    if (take.lte(0)) continue;
    shares = shares.plus(take);
    worst = priceD;
    feeShares = feeShares.plus(take.times(legCostFraction(priceD, feeRate, feeExponent, leg.feeModel)));
  }

  if (shares.lte(0) || spent.lte(0)) {
    return {
      stake: new Decimal(0),
      odds: bestOdds(leg),
      costFraction: new Decimal(0),
      shares: new Decimal(0),
      cappedBy: 'стакан пуст',
      limitPrice: null,
      avgPrice: null,
    };
  }

  let cappedBy: string | null = null;
  if (spent.lt(budgetD)) cappedBy = 'глубина стакана';
  else if (spent.lt(minOrder)) cappedBy = `меньше минимального ордера ${minOrder.toString()}`;
  else if (capped) cappedBy = 'лимит конторы';

  return {
    stake: spent,
    odds: shares.div(spent),
    costFraction: feeShares.div(shares),
    shares,
    cappedBy,
    limitPrice: worst,
    avgPrice: spent.div(shares),
  };
}

export function fillLeg(leg: Leg, budget: Odds): LegFill {
  if (leg.kind === 'book') return walkBook(leg, budget);
  const budgetD = d(budget);
  const limit = leg.limit == null ? null : d(leg.limit);
  const capped = limit !== null && budgetD.gt(limit);
  return {
    stake: capped ? limit! : budgetD,
    odds: d(leg.odds),
    costFraction: new Decimal(0),
    shares: null,
    cappedBy: capped ? 'лимит конторы' : null,
    limitPrice: null,
    avgPrice: null,
  };
}

/**
 * Раскладка банка с проходом по стакану. Итерация до стабилизации ставок.
 *
 * Ставка определяется кэфом, а кэф после прохода по стакану — ставкой. Один
 * проход завышает ROI ровно на вилках с тонким стаканом, то есть на большинстве.
 */
export function quoteAtVolume(bank: Odds, legs: readonly Leg[], passes = 8): VolumeQuote {
  const bankD = d(bank);
  if (bankD.lte(0)) throw new Error('банк должен быть положительным');
  if (legs.length < 2) throw new Error('вилка не собирается из одной ноги');

  let odds = legs.map(bestOdds);
  let fills: LegFill[] = [];
  let previous: Decimal[] | null = null;
  for (let pass = 0; pass < passes; pass += 1) {
    const split = stakes(bankD, odds);
    fills = legs.map((leg, i) => fillLeg(leg, split[i]!));
    odds = fills.map(effOdds);
    if (previous && split.every((s, i) => s.minus(previous![i]!).abs().lte('0.01'))) break;
    previous = split;
  }

  const total = fills.reduce((acc, f) => acc.plus(f.stake), new Decimal(0));
  // Гарантированный ROI — по ХУДШЕМУ исходу. Нога, не влезшая целиком, ломает
  // равенство выплат, и вилка превращается в ставку с уклоном.
  const roi = total.lte(0)
    ? new Decimal(-1)
    : fills.map(fillPayout).reduce((a, b) => (a.lt(b) ? a : b)).div(total).minus(1);

  return {
    requested: bankD,
    fills,
    roi,
    total,
    profit: total.times(roi),
    limitedBy: fills.map((f) => f.cappedBy).filter((c): c is string => c !== null),
  };
}

/**
 * Нога, которую человек набил руками. Банк и лимит конторы её не режут:
 * он ввёл то, что контора приняла или что собирается вбить.
 *
 * Цена и кэф — из стакана, если стакан есть: иначе вторая нога сядет
 * на выдуманную выплату.
 */
function lockFill(leg: Leg, stake: Decimal): LegFill {
  if (leg.kind === 'book') {
    const walked = walkBook(leg, stake);
    if (walked.stake.gt(0) && walked.avgPrice !== null && walked.avgPrice.gt(0)) {
      return {
        ...walked,
        stake,
        shares: stake.div(walked.avgPrice),
      };
    }
    const odds = bestOdds(leg);
    const price = odds.gt(0) ? new Decimal(1).div(odds) : null;
    return {
      stake,
      odds,
      costFraction: new Decimal(0),
      shares: price !== null && price.gt(0) ? stake.div(price) : new Decimal(0),
      cappedBy: walked.cappedBy,
      limitPrice: walked.limitPrice ?? price,
      avgPrice: walked.avgPrice ?? price,
    };
  }
  return {
    stake,
    odds: d(leg.odds),
    costFraction: new Decimal(0),
    shares: null,
    cappedBy: null,
    limitPrice: null,
    avgPrice: null,
  };
}

/**
 * Раскладка от набитого плеча: эта нога стоит, остальные — под равную выплату.
 *
 * Банк сюда не входит. Вторая нога может упереться в стакан или лимит —
 * тогда выплаты разъедутся, и ROI это покажет, а не спрячет.
 */
export function quoteFromAnchor(
  legs: readonly Leg[],
  index: number,
  stake: Odds,
  passes = 8,
): VolumeQuote {
  if (legs.length < 2) throw new Error('вилка не собирается из одной ноги');
  if (index < 0 || index >= legs.length) throw new Error('якоря нет в вилке');
  const stakeD = d(stake);
  if (stakeD.lte(0)) throw new Error('якорь должен быть положительным');

  const locked = lockFill(legs[index]!, stakeD);
  let odds = legs.map(bestOdds);
  odds[index] = effOdds(locked);
  const target = fillPayout(locked);

  let fills: LegFill[] = [];
  for (let pass = 0; pass < passes; pass += 1) {
    fills = legs.map((leg, i) => {
      if (i === index) return locked;
      const odd = odds[i]!;
      return fillLeg(leg, odd.gt(0) ? target.div(odd) : new Decimal(0));
    });
    const next = fills.map(effOdds);
    next[index] = effOdds(locked);
    if (odds.every((odd, i) => odd.minus(next[i]!).abs().lte('0.0001'))) break;
    odds = next;
  }

  const total = fills.reduce((acc, fill) => acc.plus(fill.stake), new Decimal(0));
  const roi = total.lte(0)
    ? new Decimal(-1)
    : fills.map(fillPayout).reduce((a, b) => (a.lt(b) ? a : b)).div(total).minus(1);

  return {
    requested: stakeD,
    fills,
    roi,
    total,
    profit: total.times(roi),
    limitedBy: fills.map((f) => f.cappedBy).filter((c): c is string => c !== null),
  };
}

/** ROI при 100 / 250 / 500 — лестница раскрытой карточки. */
export const roiLadder = (legs: readonly Leg[], banks: readonly string[] = ['100', '250', '500']): VolumeQuote[] =>
  banks.map((bank) => quoteAtVolume(bank, legs));

/** Излом прибыли: бюджет, чья нога его создала и на какой её ставке. */
interface Kink {
  budget: Decimal;
  /** Нога и её ставка на изломе. null — это не излом, а потолок объёма. */
  leg: number | null;
  edge: Decimal | null;
}

/**
 * Бюджеты, около которых у прибыли излом: границы уровней и лимиты конторы.
 *
 * Перевод ставки ноги в общий банк приблизительный — кэфы после прохода по
 * стакану уже не те, по которым считалась раскладка. Поэтому вместе с бюджетом
 * запоминается, ЧЬЯ ставка его породила: по этой паре вершина потом досаживается
 * на край точно.
 */
function kinkBudgets(legs: readonly Leg[], ceiling: Decimal): Kink[] {
  const odds = legs.map(bestOdds);
  const totalImplied = impliedSum(odds);
  const seen = new Map<string, Kink>([
    [ceiling.toString(), { budget: ceiling, leg: null, edge: null }],
  ]);

  legs.forEach((leg, i) => {
    const legStakes: Decimal[] = [];
    if (leg.kind === 'book') {
      const limit = leg.limit == null ? null : d(leg.limit);
      let spent = new Decimal(0);
      for (const [price, size] of leg.asks) {
        spent = spent.plus(d(price).times(d(size)));
        if (limit !== null && limit.gt(0) && spent.gte(limit)) break;
        legStakes.push(spent);
      }
      if (limit !== null && limit.gt(0)) legStakes.push(limit);
    } else if (leg.limit != null) {
      legStakes.push(d(leg.limit));
    }
    for (const stake of legStakes) {
      const budget = stake.times(totalImplied).times(odds[i]!);
      const key = budget.toString();
      if (budget.gte(VOLUME_TOLERANCE) && budget.lt(ceiling) && !seen.has(key)) {
        seen.set(key, { budget, leg: i, edge: stake });
      }
    }
  });

  return [...seen.values()].sort((a, b) => a.budget.comparedTo(b.budget));
}

/** Сколько раз подтягивать бюджет к краю. Первый пересчёт попадает почти точно. */
const SNAP_PASSES = 3;

/**
 * Досадить бюджет ровно на край: конец уровня стакана или лимит конторы.
 *
 * Пока нога не упёрлась в край, ROI держится, а прибыль растёт объёмом — значит
 * вершина стоит РОВНО на краю. Сетка знает край приблизительно и промахивается
 * на проценты, а промах в объёме — это ровно столько же процентов денег строки.
 */
function snapToEdge(legs: readonly Leg[], kink: Kink, ceiling: Decimal): VolumeQuote | null {
  if (kink.leg === null || kink.edge === null) return null;
  let budget = kink.budget;
  let quote = quoteAtVolume(budget, legs);
  for (let pass = 0; pass < SNAP_PASSES; pass += 1) {
    const stake = quote.fills[kink.leg]!.stake;
    if (stake.lte(0) || stake.eq(kink.edge)) break;
    budget = Decimal.min(ceiling, budget.times(kink.edge).div(stake));
    if (budget.lt(VOLUME_TOLERANCE)) break;
    quote = quoteAtVolume(budget, legs);
  }
  return quote;
}

/** Наибольшая прибыль на отрезке. Золотое сечение: производной у прибыли нет. */
function goldenMax(legs: readonly Leg[], low: Decimal, high: Decimal, tolerance: Decimal): VolumeQuote {
  const phi = new Decimal('0.6180339887');
  let left = low;
  let right = high;
  let probeA = right.minus(right.minus(left).times(phi));
  let probeB = left.plus(right.minus(left).times(phi));
  let quoteA = quoteAtVolume(probeA, legs);
  let quoteB = quoteAtVolume(probeB, legs);
  for (let step = 0; step < GOLDEN_STEPS; step += 1) {
    if (right.minus(left).lte(tolerance)) break;
    if (quoteA.profit.gte(quoteB.profit)) {
      right = probeB;
      probeB = probeA;
      quoteB = quoteA;
      probeA = right.minus(right.minus(left).times(phi));
      quoteA = quoteAtVolume(probeA, legs);
    } else {
      left = probeA;
      probeA = probeB;
      quoteA = quoteB;
      probeB = left.plus(right.minus(left).times(phi));
      quoteB = quoteAtVolume(probeB, legs);
    }
  }
  return quoteA.profit.gte(quoteB.profit) ? quoteA : quoteB;
}

/**
 * Объём, при котором вилка приносит БОЛЬШЕ ВСЕГО ДЕНЕГ. Главное число строки.
 *
 * Не ROI и не чужой банк. Процент сравнивает вилки между собой, деньги
 * сравнивают вилку с тем, чтобы не вставать со стула, и второе сравнение
 * оператор делает чаще.
 *
 * Границы уровней задают сетку, внутри лучшего отрезка ответ уточняется
 * поиском. Одним поиском обойтись нельзя: стакан с тонким дорогим уровнем между
 * двумя дешёвыми даёт две вершины, и поиск сошёлся бы к ближней. Одной сеткой —
 * тоже: пока уровень не кончился, ROI постоянен и вершина лежит внутри отрезка.
 */
export function bestVolume(
  legs: readonly Leg[],
  ceiling: Odds = DEFAULT_CEILING,
  tolerance: Odds = VOLUME_TOLERANCE,
): VolumeQuote {
  const ceilingD = d(ceiling);
  if (ceilingD.lte(0)) throw new Error('потолок объёма должен быть положительным');
  const kinks = kinkBudgets(legs, ceilingD);
  const grid = kinks.map((kink) => kink.budget);

  // Сетка обходится снизу вверх и бросается после двух спадов подряд. Один спад
  // ничего не значит: тонкий дорогой уровень проваливает прибыль, а за ним она
  // отрастает — ради таких стаканов сетка и заведена.
  const quotes: VolumeQuote[] = [];
  let falling = 0;
  for (const budget of grid) {
    const quote = quoteAtVolume(budget, legs);
    const last = quotes[quotes.length - 1];
    falling = last && quote.profit.lt(last.profit) ? falling + 1 : 0;
    quotes.push(quote);
    if (falling >= 2) break;
  }

  let peak = 0;
  quotes.forEach((q, i) => {
    if (q.profit.gt(quotes[peak]!.profit)) peak = i;
  });

  // Три ответа на один вопрос, и берётся лучший по деньгам: узел сетки как он
  // есть, тот же узел, досаженный на свой край, и вершина внутри отрезка между
  // соседями.
  let best = quotes[peak]!;
  const snapped = snapToEdge(legs, kinks[peak]!, ceilingD);
  if (snapped !== null && snapped.profit.gt(best.profit)) best = snapped;

  const toleranceD = d(tolerance);
  const low = peak > 0 ? grid[peak - 1]! : Decimal.min(toleranceD, grid[peak]!);
  const high = peak + 1 < grid.length ? grid[peak + 1]! : grid[peak]!;
  const step = Decimal.max(toleranceD, high.minus(low).div(100));
  if (high.minus(low).lte(step)) return best;
  const refined = goldenMax(legs, low, high, step);
  return refined.profit.gt(best.profit) ? refined : best;
}

/** CLV в процентах — зеркало `engine/money_rank.clv_pct`. */
export function clvPct(played: Odds, sharp: Odds): Decimal | null {
  const p = d(played);
  const s = d(sharp);
  if (p.lte(0) || s.lte(0)) return null;
  return p.div(s).minus(1).times(100);
}

/** Ожидаемые деньги строки — зеркало `engine/money_rank.money_score`. */
export function moneyScore(live60: Odds, fitProfit: Odds, clvProxy: Odds | null): Decimal {
  const live = d(live60);
  const profit = d(fitProfit);
  let factor = new Decimal(1);
  if (clvProxy !== null) {
    const bump = Decimal.max(
      new Decimal('-0.5'),
      Decimal.min(new Decimal('0.5'), d(clvProxy).div(100)),
    );
    factor = factor.plus(bump);
  }
  const raw = live.times(profit).times(factor);
  if (raw.lte(0)) return new Decimal(0);
  return raw.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/** Коридор: break-even доля попаданий. Зеркало `middles.break_even`. */
export function middleBreakEven(cost: Odds, payout: Odds): Decimal | null {
  const denom = d(cost).plus(d(payout));
  if (denom.lte(0)) return null;
  return d(cost).div(denom);
}

/** Коридор: ожидание в долях банка. Зеркало `middles.ev`. */
export function middleEv(cost: Odds, payout: Odds, pHit: Odds): Decimal {
  const p = d(pHit);
  return p.times(d(payout)).minus(new Decimal(1).minus(p).times(d(cost)));
}

/** Коридор: ожидание в деньгах банка. */
export function middleEvMoney(cost: Odds, payout: Odds, pHit: Odds, bank: Odds): Decimal {
  if (d(bank).lte(0)) return new Decimal(0);
  return middleEv(cost, payout, pHit).times(d(bank));
}

/** Исполняемый размер: min(банк, лимит, глубина), вниз по шагу. */
export function middleQStar(
  bank: Odds,
  limit?: Odds | null,
  depth?: Odds | null,
  step?: Odds | null,
): Decimal {
  let size = d(bank);
  for (const cap of [limit, depth]) {
    if (cap !== undefined && cap !== null && d(cap).gte(0)) {
      size = Decimal.min(size, d(cap));
    }
  }
  if (step !== undefined && step !== null && d(step).gt(0)) {
    size = size.div(d(step)).toDecimalPlaces(0, Decimal.ROUND_DOWN).times(d(step));
  }
  return size.gt(0) ? size : new Decimal(0);
}
